import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getStoredConsent, storeConsent } from "@/lib/tracking";

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(true);

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const decide = (consent) => {
    storeConsent(consent);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 inset-x-0 z-[60] p-4 md:p-6"
        >
          <div className="max-w-[1400px] mx-auto backdrop-blur-xl bg-brand-ink/90 border border-gold/30 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
            <div className="p-6 md:p-8">
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <Cookie className="text-gold shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-serif text-xl text-white mb-1">Votre souveraineté numérique</h3>
                    <p className="text-sm text-white/60 font-light leading-relaxed max-w-2xl">
                      Nous utilisons des cookies pour la mesure d'audience et la performance publicitaire.
                      Vous gardez le contrôle total de vos données.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <button
                    data-testid="cookie-refuse"
                    onClick={() => decide({ analytics: false, ads: false })}
                    className="border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-colors px-6 py-3 text-xs uppercase tracking-[0.15em]"
                  >
                    Refuser
                  </button>
                  <button
                    data-testid="cookie-customize"
                    onClick={() => setCustomize((v) => !v)}
                    className="border border-gold/40 text-gold hover:bg-gold/10 transition-colors px-6 py-3 text-xs uppercase tracking-[0.15em]"
                  >
                    Personnaliser
                  </button>
                  <button
                    data-testid="cookie-accept"
                    onClick={() => decide({ analytics: true, ads: true })}
                    className="bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold"
                  >
                    Accepter
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {customize && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-6 pt-6 border-t border-white/10 grid md:grid-cols-2 gap-5">
                      <div className="flex items-center justify-between gap-4 p-4 border border-white/10">
                        <div>
                          <p className="text-sm text-white flex items-center gap-2">
                            <ShieldCheck size={15} className="text-gold" /> Mesure d'audience (GA4)
                          </p>
                          <p className="text-xs text-white/40 mt-1">Statistiques de visite anonymisées.</p>
                        </div>
                        <Switch
                          data-testid="consent-analytics-switch"
                          checked={analytics}
                          onCheckedChange={setAnalytics}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4 p-4 border border-white/10">
                        <div>
                          <p className="text-sm text-white flex items-center gap-2">
                            <ShieldCheck size={15} className="text-gold" /> Publicité (Google Ads / Meta)
                          </p>
                          <p className="text-xs text-white/40 mt-1">Personnalisation & mesure des campagnes.</p>
                        </div>
                        <Switch
                          data-testid="consent-ads-switch"
                          checked={ads}
                          onCheckedChange={setAds}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        data-testid="cookie-save-preferences"
                        onClick={() => decide({ analytics, ads })}
                        className="bg-white text-brand-ink hover:bg-white/90 transition-colors px-8 py-3 text-xs uppercase tracking-[0.15em] font-semibold"
                      >
                        Enregistrer mes choix
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
